import React, { useEffect, useState, useCallback } from 'react';
import { SxStyle } from '../../../types';
import StaffOverviewHeader from './components/staff-overview-header';
import { useStringsContext } from '../../../contexts/strings';
import Box from '@mui/material/Box';
import { useHistory } from 'react-router-dom';
import { useStaff } from './hooks/staff';
import StaffList from './components/staff-list';
import StaffMemberModal from './components/staff-member-modal';
import RemoveStaffMemberModal from './components/remove-staff-member-modal';
import { StaffMemberInternal } from './types';
import { StaffMember } from './api-types';
import UploadStaffImagesModal from './components/upload-staff-images-modal';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import ScenePage from '../../common/scene/scene-page';

const styles: SxStyle = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
};

interface Props {
  match: any; // TODO fix type
}

function StaffContainer({ match }: Props) {
  const { teamId, season } = match.params;
  const { strings } = useStringsContext();
  const breadCrumps = useBreadcrumbs();
  const history = useHistory();
  const { fetchStaff } = useStaff();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [addMember, setAddMember] = useState(false);
  const [editMember, setEditMember] = useState<StaffMember | null>();
  const [removeMember, setRemoveMember] = useState<StaffMemberInternal | null>(null);
  const [staff, setStaff] = useState<StaffMemberInternal[] | null>(null);
  const [showUploadImages, setShowUploadImages] = useState(false);

  const getStaff = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const staff = await fetchStaff(teamId, season);
      setStaff(staff);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [season, fetchStaff, teamId]);

  useEffect(() => {
    getStaff();
  }, [teamId, season, getStaff]);

  const onEditMember = (member: StaffMemberInternal) => {
    setEditMember(member);
  };

  const onSelectSeason = (value: string) => {
    history.push(`/staff/${teamId}/${value}`);
  };

  const onDeleteMember = (member: StaffMemberInternal) => {
    setRemoveMember(member);
  };

  return (
    <ScenePage
      title={strings.menu.staff}
      breadCrumps={[...breadCrumps, { text: season, to: `/staff/${teamId}/season` }]}
    >
      <Box sx={styles.container}>
        {!error && (
          <>
            <StaffOverviewHeader
              season={season}
              onAddMember={() => setAddMember(true)}
              onSelectSeason={onSelectSeason}
              onUploadImages={() => setShowUploadImages(true)}
            />
            <StaffList
              staff={staff}
              onAdd={() => setAddMember(true)}
              onEdit={onEditMember}
              onDelete={onDeleteMember}
              loading={loading}
            />
          </>
        )}
        {editMember && (
          <StaffMemberModal
            open
            onClose={() => {
              setEditMember(null);
            }}
            season={season}
            teamId={teamId}
            member={editMember as StaffMember}
            onDone={getStaff}
          />
        )}
        {addMember && (
          <StaffMemberModal
            open
            onClose={() => {
              setAddMember(false);
            }}
            season={season}
            teamId={teamId}
            onDone={getStaff}
          />
        )}
        {removeMember && (
          <RemoveStaffMemberModal
            open
            onClose={() => setRemoveMember(null)}
            onRemoved={getStaff}
            member={removeMember}
            season={season}
            teamId={teamId}
          />
        )}
        {showUploadImages && staff && (
          <UploadStaffImagesModal
            open={showUploadImages}
            onClose={() => setShowUploadImages(false)}
            staff={staff}
            teamId={teamId}
            season={season}
            onSubmitted={getStaff}
          />
        )}
      </Box>
    </ScenePage>
  );
}

export default StaffContainer;
